'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion'

/* ─────────────────────────────────────────────────────────
 *  Story steps — each is a compensation snapshot.
 *  The donut transitions through these to show how total
 *  comp evolves across real career events.
 * ──────────────────────────────────────────────────────── */
interface Segment { label: string; amount: number; color: string }
interface Step { title: string; subtitle: string; segments: Segment[] }

const steps: Step[] = [
  {
    title: 'E-5 — Fort Hood, TX',
    subtitle: 'Monthly base pay at current rank',
    segments: [
      { label: 'Base', amount: 3306, color: '#2563eb' },
    ],
  },
  {
    title: 'Basic Allowance for Housing',
    subtitle: 'BAH rate based on duty station ZIP',
    segments: [
      { label: 'Base', amount: 3306, color: '#2563eb' },
      { label: 'BAH', amount: 1617, color: '#3b82f6' },
    ],
  },
  {
    title: 'Basic Allowance for Subsistence',
    subtitle: 'BAS completes your monthly compensation',
    segments: [
      { label: 'Base', amount: 3306, color: '#2563eb' },
      { label: 'BAH', amount: 1617, color: '#3b82f6' },
      { label: 'BAS', amount: 452, color: '#60a5fa' },
    ],
  },
  {
    title: 'PCS to San Diego, CA',
    subtitle: 'BAH adjusts to local cost of living',
    segments: [
      { label: 'Base', amount: 3306, color: '#2563eb' },
      { label: 'BAH', amount: 2997, color: '#3b82f6' },
      { label: 'BAS', amount: 452, color: '#60a5fa' },
    ],
  },
  {
    title: 'Promoted to E-6',
    subtitle: 'Base pay increases with rank',
    segments: [
      { label: 'Base', amount: 3849, color: '#2563eb' },
      { label: 'BAH', amount: 2997, color: '#3b82f6' },
      { label: 'BAS', amount: 452, color: '#60a5fa' },
    ],
  },
  {
    title: 'Civilian equivalent value',
    subtitle: 'Tax-free allowances increase your true earning power',
    segments: [
      { label: 'Base', amount: 3849, color: '#2563eb' },
      { label: 'BAH', amount: 2997, color: '#3b82f6' },
      { label: 'BAS', amount: 452, color: '#60a5fa' },
      { label: 'Tax Adv.', amount: 920, color: '#10b981' },
    ],
  },
]

const STEP_DURATION = 4400
const PAUSE_AT_END = 5000

/* ── Donut geometry ───────────────────────────────────── */
const RADIUS = 120
const STROKE = 28
const PAD = 100 // extra space around donut for callout labels + text
const CX = RADIUS + STROKE / 2 + PAD
const CY = RADIUS + STROKE / 2 + PAD
const VB = (RADIUS + STROKE / 2) * 2 + PAD * 2 // full viewBox size
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const LABEL_LINE_LEN = 30 // how far the callout extends from the arc
const LABEL_TICK = 16 // horizontal tick length at end of callout
// Max total across all steps — arcs are sized relative to this so
// segments keep a stable size when only *other* segments change.
const MAX_TOTAL = Math.max(...steps.map((s) => s.segments.reduce((sum, seg) => sum + seg.amount, 0)))

/* ── Animated counter ─────────────────────────────────── */
function AnimatedTotal({ value, className }: { value: number; className?: string }) {
  const motionVal = useMotionValue(0)
  const display = useTransform(motionVal, (v) => `$${Math.round(v).toLocaleString()}`)
  const [text, setText] = useState('$0')

  useEffect(() => {
    const unsub = display.on('change', (v) => setText(v))
    return unsub
  }, [display])

  useEffect(() => {
    const ctrl = animate(motionVal, value, {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1],
    })
    return () => ctrl.stop()
  }, [value, motionVal])

  return <span className={className}>{text}</span>
}

/* ── Main component ───────────────────────────────────── */
export default function HeroDashboardAnimation() {
  const [stepIdx, setStepIdx] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const [prefersReduced, setPrefersReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReduced(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const advance = useCallback(() => {
    setStepIdx((prev) => {
      const next = prev + 1
      if (next >= steps.length) return -1
      return next
    })
  }, [])

  useEffect(() => {
    if (prefersReduced) { setStepIdx(steps.length - 1); return }
    if (stepIdx === -1) {
      timerRef.current = setTimeout(() => setStepIdx(0), PAUSE_AT_END)
      return () => clearTimeout(timerRef.current)
    }
    const isLast = stepIdx === steps.length - 1
    timerRef.current = setTimeout(advance, isLast ? PAUSE_AT_END : STEP_DURATION)
    return () => clearTimeout(timerRef.current)
  }, [stepIdx, prefersReduced, advance])

  const activeIdx = stepIdx === -1 ? steps.length - 1 : stepIdx
  const step = steps[activeIdx]
  const total = step.segments.reduce((s, seg) => s + seg.amount, 0)
  const isLastStep = activeIdx === steps.length - 1

  const arcs = step.segments.map((seg, i) => {
    const fraction = seg.amount / MAX_TOTAL
    const dashLen = fraction * CIRCUMFERENCE
    const dashGap = CIRCUMFERENCE - dashLen
    const preceding = step.segments.slice(0, i).reduce((s, p) => s + p.amount, 0)
    const offset = -(preceding / MAX_TOTAL) * CIRCUMFERENCE

    // Midpoint angle for callout line (0 = top, clockwise)
    const midFraction = (preceding + seg.amount / 2) / MAX_TOTAL
    const angle = midFraction * 2 * Math.PI - Math.PI / 2 // -π/2 so 0 fraction = top
    const outerR = RADIUS + STROKE / 2
    const lineStartX = CX + Math.cos(angle) * outerR
    const lineStartY = CY + Math.sin(angle) * outerR
    const lineEndX = CX + Math.cos(angle) * (outerR + LABEL_LINE_LEN)
    const lineEndY = CY + Math.sin(angle) * (outerR + LABEL_LINE_LEN)
    const isRight = lineEndX >= CX
    const tickEndX = lineEndX + (isRight ? LABEL_TICK : -LABEL_TICK)
    const textAnchor = isRight ? 'start' as const : 'end' as const

    return { ...seg, dashLen, dashGap, offset, lineStartX, lineStartY, lineEndX, lineEndY, tickEndX, textAnchor }
  })

  return (
    <div className="relative flex flex-col items-center">
      {/* ── donut ring + callout labels ─────────── */}
      <div className="relative w-full" style={{ maxWidth: 460, aspectRatio: '1 / 1' }}>
        {/* donut arcs (rotated -90 so 0° = top) */}
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          className="absolute inset-0 h-full w-full -rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={CX} cy={CY} r={RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={STROKE}
          />
          {arcs.map((arc) => (
            <motion.circle
              key={arc.label}
              cx={CX} cy={CY} r={RADIUS}
              fill="none"
              stroke={arc.color}
              strokeWidth={STROKE}
              strokeLinecap="butt"
              initial={{ strokeDasharray: `0 ${CIRCUMFERENCE}`, strokeDashoffset: 0 }}
              animate={{
                strokeDasharray: `${arc.dashLen} ${arc.dashGap}`,
                strokeDashoffset: arc.offset,
              }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
          ))}
        </svg>

        {/* callout lines + labels (not rotated) */}
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <AnimatePresence>
            {arcs.map((arc) => (
              <motion.g
                key={arc.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {/* radial line */}
                <line
                  x1={arc.lineStartX} y1={arc.lineStartY}
                  x2={arc.lineEndX} y2={arc.lineEndY}
                  stroke={arc.color}
                  strokeWidth={1.5}
                  opacity={0.6}
                />
                {/* horizontal tick */}
                <line
                  x1={arc.lineEndX} y1={arc.lineEndY}
                  x2={arc.tickEndX} y2={arc.lineEndY}
                  stroke={arc.color}
                  strokeWidth={1.5}
                  opacity={0.6}
                />
                {/* label text */}
                <text
                  x={arc.tickEndX + (arc.textAnchor === 'start' ? 5 : -5)}
                  y={arc.lineEndY}
                  textAnchor={arc.textAnchor}
                  dominantBaseline="middle"
                  fill="rgba(255,255,255,0.85)"
                  fontSize={13}
                  fontWeight={600}
                  fontFamily="system-ui, sans-serif"
                  letterSpacing="0.01em"
                >
                  {arc.label}
                </text>
              </motion.g>
            ))}
          </AnimatePresence>
        </svg>

        {/* center total */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            className="text-4xl font-bold tabular-nums leading-none sm:text-5xl"
            animate={{ color: isLastStep ? '#10b981' : '#f8fafc' }}
            transition={{ duration: 0.5 }}
          >
            <AnimatedTotal value={total} />
          </motion.span>
          <span className="mt-1 text-xs font-medium text-white/35">/month</span>
        </div>
      </div>

      {/* ── storytelling copy (below donut, right-aligned) ── */}
      <div className="w-full" style={{ maxWidth: 460 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="min-h-[48px] text-right"
          >
            <p className="text-base font-semibold text-white">{step.title}</p>
            <p className="mt-0.5 text-sm text-white/45">{step.subtitle}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── progress dots ───────────────────────── */}
      <div className="mt-4 flex justify-center gap-1.5">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === activeIdx
                ? 'w-4 bg-blue-400'
                : i < activeIdx
                  ? 'w-1.5 bg-white/20'
                  : 'w-1.5 bg-white/8'
            }`}
          />
        ))}
      </div>

      {/* ── ambient glow behind donut ───────────── */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/8 blur-3xl" />
    </div>
  )
}
